/**
 * Centralized locale-aware date formatting.
 * Maps i18n language codes to date-fns locales and provides
 * wrapper functions that automatically use the current language.
 */
import { formatDistanceToNow as fdfn, format as dfFormat, type Locale } from 'date-fns';
import { enUS, fr, zhCN, arSA } from 'date-fns/locale';
import i18n from '@/i18n';

// Static locale map – eagerly loaded locales
const localeMap: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  zh: zhCN,
  ar: arSA,
  // date-fns has no Swahili, Amharic, Somali, or Dinka locales – fall back to English
  sw: enUS,
  am: enUS,
  so: enUS,
  din: enUS,
};

/**
 * Get the currently resolved date-fns locale based on i18n language.
 */
function getCurrentLocale(): Locale {
  const lang = i18n.language?.split('-')[0] || 'en';
  return localeMap[lang] || enUS;
}

/**
 * Locale-aware wrapper for date-fns formatDistanceToNow.
 */
export function formatDistanceToNowLocale(
  date: Date | number | string,
  options?: { addSuffix?: boolean }
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return fdfn(d, { ...options, locale: getCurrentLocale() });
}

/**
 * Locale-aware wrapper for date-fns format.
 */
export function formatLocale(
  date: Date | number | string,
  formatStr: string
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dfFormat(d, formatStr, { locale: getCurrentLocale() });
}

/**
 * Pre-load the locale for the given language code.
 * Call on language change so subsequent renders use the correct locale.
 */
export function preloadDateLocale(langCode: string): void {
  const code = langCode.split('-')[0];
  // Just ensure it's in the map (all are statically assigned, so this is instant)
  if (!localeMap[code]) {
    localeMap[code] = enUS;
  }
}
